// ChargeModelCard — displays product charge breakdown (one-time + recurring).
// Inline styles only — no Tailwind. Surface is the shared EmbedCard primitive;
// colors read from the active TokenSet via useEmbedTokens() (Phase-0 migration).
//
// Two mutually-exclusive input shapes (R2 catalog-display redesign, design
// doc §5): `chargeModel` is the legacy `charges[]`-row shape (TD-308) — a
// SINGLE row that may carry both a one-time AND a recurring amount at once.
// `components` is the new `price.components[]` shape (Task 4) — each entry
// is EXACTLY one charge_type, never both, so it renders one row per
// component. `components` takes priority when present and non-empty; a row's
// value is formatted via `formatComponent` — the same per-component formatter
// `formatPriceHeadline` resolves through — so this card's suffix wording
// (unit/interval) stays byte-identical to the PDP headline's.
//
// ⚠️ AMENDED (fix wave 2026-09-15, round 2). ORIGINAL: each row called
// `formatPriceHeadline({ components: [c] }, null, currency)`. That reached
// `pickHeadline`'s SELECTION filters, and ITEM B's device-basis one-time
// exclusion made every such component format to null — so a device one-time
// cell was silently dropped from the breakdown entirely. Selection is not
// rendering: this card renders what the operator configured.
//
// ⚠️ AMENDED (device-count unit, Task 10, 2026-09-17) — the ITEM C
// row-suppression filter (`isCurrentlyBillable`) is RETIRED. It excluded a
// device-basis ONE-TIME component because `ResolveCellPrice` multiplied it
// by a device count that was always 0 — a rate stated as a charge that
// billed $0.00. The device-count unit made quantity the device count
// (operator ruling 1, Tasks 3/3.5/3.75), so the component bills quantity ×
// rate for real and belongs in the breakdown UNFILTERED — leaving the filter
// in would keep device-priced products silently priceless on this surface.
// See the REMOVED record in product-display.ts.

import { useEmbedTokens, EmbedCard } from "../../embed-shared/primitives";
import { formatCurrency } from "../../embed-shared/currency";
import { formatComponent } from "./product-display";
import { useStorefrontConfig } from "./storefront-config";
import type { ChargeModel, PriceComponent } from "../../embed-shared/types";

// Fix round 1, MINOR 3 (CONTROLLER RULING): a plain `{chargeModel?; components?}`
// shape let BOTH be omitted (or both supplied) and still compile — a
// plausible-looking-wrong UI (`<ChargeModelCard />` silently rendering "No
// charges configured") where the compiler used to catch the omission when
// `chargeModel` was required. The discriminated union restores that
// compile-time guarantee — the same category of fix as Task 4's dead-field
// deletion making its own guard structural rather than conventional.
export type ChargeModelCardProps =
  | { chargeModel: ChargeModel; components?: never }
  | { components: PriceComponent[]; chargeModel?: never };

interface Row {
  key: string;
  label: string;
  value: string;
  color: "one_time" | "recurring";
}

export function ChargeModelCard({ chargeModel, components }: ChargeModelCardProps) {
  const t = useEmbedTokens();
  // ITEM A fix (fix wave 2026-09-15, operator ruling 2026-09-15): currency is
  // marketplace-wide, never per-product — this card is a product-display
  // formatting site and derives it from StorefrontConfig context, the same
  // idiom ProductCard.tsx/ProductDetailView.tsx use, rather than trusting
  // `chargeModel.currency` (the legacy per-charge wire field — kept on the
  // `ChargeModel` type for structural compatibility at the time, never read
  // here, and REMOVED from the type by TD-513 Task 4, 2026-09-19) or a
  // per-component currency (`PriceComponent` deliberately carries no
  // currency field — see the rejected-fix pinning test in
  // product-display.test.ts).
  const { currency } = useStorefrontConfig();

  const rows: Row[] = [];
  if (components && components.length > 0) {
    components.forEach((c, i) => {
      // (Task 10 removed the ITEM C device-basis one-time skip here — every
      // component that resolves renders; see the header's AMENDED note.)
      // Minor fix (fix wave 2026-09-15): formatComponent returns null for
      // a component with an unrecognized recurring_interval (fails closed
      // per-component). `?? ""` used to still push the row with its label
      // ("Recurring") and a BLANK amount — a customer-visible half-row that
      // states a charge type with no price. Exclude the row entirely instead
      // — a missing row reads better than a broken one. Rule 42: the
      // anomaly is still console.error'd — the sole caller (ProductDetailView)
      // renders this card only when `hasPriceComponents`, and that same
      // condition already ran formatPriceHeadline over the SAME array, whose
      // resolveComponents logs it.
      const value = formatComponent(c, currency);
      if (value == null) return;
      rows.push({
        key: `${c.charge_type}-${c.basis}-${i}`,
        label: c.charge_type === "recurring" ? "Recurring" : "One-time",
        value,
        color: c.charge_type,
      });
    });
  } else if (chargeModel) {
    const { one_time_amount, recurring_amount, recurring_interval } = chargeModel;
    if (one_time_amount > 0) {
      rows.push({ key: "one-time", label: "One-time", value: formatCurrency(one_time_amount, currency), color: "one_time" });
    }
    if (recurring_amount > 0) {
      rows.push({
        key: "recurring",
        label: `Recurring (${recurring_interval})`,
        value: formatCurrency(recurring_amount, currency),
        color: "recurring",
      });
    }
  }

  return (
    <EmbedCard padding={t.spacing.md}>
      <h4
        style={{
          fontSize: t.fontSize.sm,
          fontWeight: 600,
          color: t.colors.text,
          margin: `0 0 ${t.spacing.sm}px 0`,
        }}
      >
        Charge breakdown
      </h4>
      <div style={{ display: "flex", flexDirection: "column", gap: t.spacing.xs }}>
        {rows.length > 0 ? (
          rows.map((row) => (
            <div key={row.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: t.fontSize.sm, color: t.colors.textSecondary }}>
                {row.label}
              </span>
              <span
                style={{
                  fontSize: t.fontSize.sm,
                  fontWeight: 600,
                  color: row.color === "recurring" ? t.colors.chargeRecurring : t.colors.chargeOneTime,
                }}
              >
                {row.value}
              </span>
            </div>
          ))
        ) : (
          <span style={{ fontSize: t.fontSize.sm, color: t.colors.textSecondary }}>
            No charges configured
          </span>
        )}
      </div>
    </EmbedCard>
  );
}
